import React, { useEffect, useRef } from 'react';
import { useMicrophoneStore } from '../stores/microphoneStore';

interface MicrophoneRingProps {
  size?: number;
  strokeWidth?: number;
  className?: string;
}

const MicrophoneRing: React.FC<MicrophoneRingProps> = ({
  size = 192, // Default size to match the camera preview
  strokeWidth = 6,
  className = '',
}) => {
  const { isMicOn, microphoneStream } = useMicrophoneStore();
  const audioRingRef = useRef<SVGCircleElement>(null);
  const micAnimationRef = useRef<number | null>(null);

  useEffect(() => {
    let audioContext: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let dataArray: Uint8Array;
    let source: MediaStreamAudioSourceNode | null = null;

    function animate() {
      if (analyser && audioRingRef.current) {
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const val = (dataArray[i] - 128) / 128;
          sum += val * val;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        const radius = size / 2 - strokeWidth / 2;
        const circumference = 2 * Math.PI * radius;
        const offset =
          circumference * (1 - Math.min(1, Math.log10(1 + rms * 19)));
        audioRingRef.current.style.strokeDashoffset = `${offset}`;
      }
      micAnimationRef.current = requestAnimationFrame(animate);
    }

    if (isMicOn && microphoneStream) {
      try {
        audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        dataArray = new Uint8Array(analyser.fftSize);
        source = audioContext.createMediaStreamSource(microphoneStream);
        source.connect(analyser);
        animate();
      } catch (error) {
        console.error('Error setting up audio analysis:', error);
      }
    }

    return () => {
      if (micAnimationRef.current) {
        cancelAnimationFrame(micAnimationRef.current);
      }
      if (source) source.disconnect();
      if (analyser) analyser.disconnect();
      if (audioContext) audioContext.close();
      if (audioRingRef.current) {
        const radius = size / 2 - strokeWidth / 2;
        const circumference = 2 * Math.PI * radius;
        audioRingRef.current.style.strokeDashoffset = `${circumference}`;
      }
    };
  }, [isMicOn, microphoneStream, size, strokeWidth]);

  if (!isMicOn) {
    return null;
  }

  const radius = size / 2 - strokeWidth / 2;
  const center = size / 2;

  return (
    <svg
      width={size}
      height={size}
      className={`absolute inset-0 ${className}`}
      style={{ zIndex: 1 }}
    >
      {/* Background ring */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        stroke="#e5e7eb"
        strokeWidth={strokeWidth}
        fill="none"
      />
      {/* Foreground (audio) ring */}
      <circle
        ref={audioRingRef}
        cx={center}
        cy={center}
        r={radius}
        stroke="#22c55e"
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={2 * Math.PI * radius}
        strokeDashoffset={2 * Math.PI * radius}
        style={{
          transition: 'stroke-dashoffset 0.15s linear',
          transform: 'rotate(-90deg)',
          transformOrigin: `${center}px ${center}px`,
        }}
      />
    </svg>
  );
};

export default MicrophoneRing;
